// Poll-pauze zodra niemand kijkt — auditbevinding #69 (dashboard_pollfrequentie).
//
// Het dashboard pollt de API met korte intervallen: de snapshot elke 5s, de
// force-actions elke 5s, shadow-commands elke 15s, en nog een stuk of twintig
// bronnen daaronder. Eén open tech-tab is daarmee ruim 200 requests per vijf
// minuten, en die frequentie is op zichzelf verdedigbaar: een EMS-dashboard
// dat de accu volgt moet vers zijn.
//
// Wat er niet aan klopte is de DUUR. Er zat nergens een rem op: een tab die
// blijft openstaan op een geminimaliseerd venster, een tweede monitor die uit
// gaat, of een telefoon in de broekzak pollde onverminderd door — 's nachts,
// in het weekend, tot de browser wordt afgesloten. De kosten van een geopend
// dashboard waren dus niet begrensd door hoe lang iemand kijkt, maar door hoe
// lang de tab bestaat, en dat zijn twee heel verschillende dingen.
//
// `document.visibilityState` is precies de grens die we willen. Hij is
// 'visible' zolang de tab op een zichtbaar scherm staat — ook op een
// wandmonitor waar op dat moment niemand naar kijkt. Alleen een
// achtergrondtab, een geminimaliseerd venster of een slapend scherm telt als
// 'hidden'. Een dashboard dat de hele dag bewust zichtbaar hangt blijft dus
// gewoon pollen; dat is bedoeld gedrag en geen regressie.
//
// Bij terugkeer naar zichtbaar wordt er direct opnieuw gehaald, niet pas na
// het volgende interval. Anders zou de pauze de gebruiker oude cijfers laten
// zien op het moment dat hij juist gaat kijken, en dat is op een EMS-dashboard
// erger dan de requests die we ermee besparen.

/** True zolang de tab op een zichtbaar scherm staat.
 *
 *  Buiten een browser (vitest zonder document, server-side render) altijd
 *  true, zodat pollers daar hun normale pad lopen in plaats van stil te
 *  vallen.
 */
export function isDocumentVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

/** Abonneer op zichtbaarheidswissels. Geeft de opzegger terug. */
export function onVisibilityChange(cb: () => void): () => void {
  if (typeof document === 'undefined') return () => {};
  document.addEventListener('visibilitychange', cb);
  return () => document.removeEventListener('visibilitychange', cb);
}

/** `setInterval` dat stilstaat zolang de tab verborgen is.
 *
 *  Bij terugkeer naar zichtbaar draait `fn` meteen één keer en loopt het
 *  interval weer. Geeft een opzegger terug die zowel het interval als de
 *  listener opruimt — bedoeld om rechtstreeks in een `useEffect`-cleanup te
 *  hangen, zodat de aanroepende component verder niets hoeft te weten.
 *
 *  Doet zelf GEEN eerste aanroep, net als `setInterval`. De aanroepers deden
 *  hun mount-fetch al vóór het interval; die volgorde blijft zo intact.
 */
export function visibleInterval(fn: () => void, intervalMs: number): () => void {
  let id: ReturnType<typeof setInterval> | undefined;

  const start = () => {
    if (id === undefined) id = setInterval(fn, intervalMs);
  };
  const stop = () => {
    if (id !== undefined) {
      clearInterval(id);
      id = undefined;
    }
  };
  const sync = () => {
    if (isDocumentVisible()) {
      fn();
      start();
    } else {
      stop();
    }
  };

  if (isDocumentVisible()) start();
  const off = onVisibilityChange(sync);

  return () => {
    stop();
    off();
  };
}
